import { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { useRates } from "@/context/RateContext";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { toast } from "sonner";
import { Minus, Plus, ScanLine, Trash2, Printer, MessageCircle, Percent, RefreshCw, Camera, Zap } from "lucide-react";
import QuickCustomerAdd from "@/components/QuickCustomerAdd";
import BarcodeScannerModal from "@/components/BarcodeScannerModal";
import { openWhatsApp, buildWhatsAppApiUrl } from "@/lib/whatsapp";
import WhatsAppReceiptModal from "@/components/WhatsAppReceiptModal";
import PayTRLinkModal from "@/components/PayTRLinkModal";
import { buildReceiptText, currentUiLang } from "@/lib/receipt";
import POSToolsDrawer from "@/components/POSToolsDrawer";
import { sanitizeDecimal, sanitizePhone, formatPhoneTR } from "@/lib/validators";
import { tokenStorage } from "@/lib/tokenStorage";

const fmt2 = (n) => Number(n ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt4 = (n) => Number(n ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 4, maximumFractionDigits: 4 });

// Harem coin fallback chain — some codes may not be in the live feed (e.g. TAM_YENI, CUMHURIYET).
// In that case fall back to a close-equivalent coin so the user never sees a wrong price.
// Object bazlı: {code, mult?} — Ata Beşli için 5×, İkibuçuk için 2.5×.
const HAREM_FALLBACK = {
  TAM_YENI:      [{ code: "ATA_YENI" }],
  TAM_ESKI:      [{ code: "TAM_YENI" }, { code: "ATA_YENI" }],
  CUMHURIYET:    [{ code: "ATA_YENI" }, { code: "TAM_YENI" }],
  CUMHURIYET_ESKI: [{ code: "CUMHURIYET" }, { code: "ATA_YENI" }],
  CEYREK_ESKI:   [{ code: "CEYREK_YENI" }],
  YARIM_ESKI:    [{ code: "YARIM_YENI" }],
  ATA_ESKI:      [{ code: "ATA_YENI" }],
  RESAT_YENI:    [{ code: "ATA_YENI" }],
  RESAT_ESKI:    [{ code: "ATA_YENI" }],
  HAMIT_YENI:    [{ code: "ATA_YENI" }],
  HAMIT_ESKI:    [{ code: "ATA_YENI" }],
  GREMESE_ESKI:  [{ code: "GREMESE_YENI" }],
  BESLIYENI:     [{ code: "ATA_YENI", mult: 5 }],
  BESLIESKI:     [{ code: "ATA_YENI", mult: 5 }],
  IKIBUCUKYENI:  [{ code: "ATA_YENI", mult: 2.5 }],
  IKIBUCUKESKI:  [{ code: "ATA_YENI", mult: 2.5 }],
};

// Gram-bazlı Harem kodları: fiyat = karat_gram_price × net_weight.
const GRAM_BASIS_HAREM_CODES = new Set([
  "ALTIN", "AYAR22", "AYAR18", "AYAR14", "KULCEALTIN", "GUMUSTRY",
]);

const resolveHaremPrice = (haremCode, karats) => {
  if (!haremCode || !karats) return null;
  const direct = karats[haremCode]?.selling;
  if (direct && direct > 0) return { price: direct, code: haremCode };
  const fallbacks = HAREM_FALLBACK[haremCode] || [];
  for (const alt of fallbacks) {
    const p = karats[alt.code]?.selling;
    if (p && p > 0) return { price: p * (alt.mult || 1), code: alt.code };
  }
  return null;
};

// Apply per-product margin AND shop-wide global profit margin (percent).
// For seed products with harem_code set:
//   • gram-bazlı kodlarda (ALTIN, AYAR22, GUMUSTRY vb.) fiyat = live_price × net_weight
//   • birim-bazlı (coin/ziynet/fx) fiyat = live_price direkt
const priceOf = (p, rates, globalMarginPct = 0, karats = null) => {
  if (p.manual_price != null && Number(p.manual_price) > 0) {
    return Number(Number(p.manual_price).toFixed(2));
  }
  if (p.harem_code) {
    const hit = resolveHaremPrice(p.harem_code, karats);
    if (hit) {
      const isGramBasis = GRAM_BASIS_HAREM_CODES.has(p.harem_code);
      const price = isGramBasis ? hit.price * (Number(p.net_weight) || 0) : hit.price;
      return Number(price.toFixed(2));
    }
    // If Harem feed is momentarily unavailable → fall back to formula so cashier never gets stuck.
  }
  const rate = rates?.[p.metal_type]?.selling_price || 0;
  const raw = p.net_weight * p.purity * rate;
  const withProductMargin = (raw + p.workmanship_cost) * (1 + p.margin_rate);
  return Number((withProductMargin * (1 + Number(globalMarginPct || 0) / 100)).toFixed(2));
};

export default function POS() {
  const { rates, karats } = useRates();
  const { user } = useAuth();
  const { t } = useI18n();
  const { profitMargin } = useSubscription();
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [cart, setCart] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [payment, setPayment] = useState("cash");
  const [type, setType] = useState("sale");
  const [scrapGram, setScrapGram] = useState("");
  const [scrapPurity, setScrapPurity] = useState("0.916");
  const [scrapMargin, setScrapMargin] = useState("0"); // Alım kârı (%): pozitif = Harem alışın altında al
  const [scrapManualTotal, setScrapManualTotal] = useState(""); // Manuel pazarlık tutarı
  const [scrapManualMode, setScrapManualMode] = useState(false);
  const [lastTxn, setLastTxn] = useState(null); // { id, total, gram, items, kind }
  const lastTxnId = lastTxn?.id;

  const [shopName, setShopName] = useState("Kuyumcunuz");
  const [shopMeta, setShopMeta] = useState({ address: "", phone: "", tax_no: "", footer: "" });
  const [scannerOpen, setScannerOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [waModal, setWaModal] = useState({ open: false, phone: "", lastTxn: null, customer: "" });
  const [paytrModal, setPaytrModal] = useState({ open: false, amount: 0, data: null, loading: false, error: null });
  useEffect(() => {
    api.get("/tenant").then(r => {
      setShopName(r.data?.shop_name || "Kuyumcunuz");
      setShopMeta({
        address: r.data?.shop_address || "",
        phone: r.data?.shop_phone || "",
        tax_no: r.data?.shop_tax_no || "",
        footer: r.data?.shop_receipt_footer || "",
      });
    }).catch(()=>{});
  }, []);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/customers").then((r) => setCustomers(r.data)).catch(()=>{});
  }, []);

  const pickCustomer = (cid) => {
    setCustomerId(cid);
    const c = customers.find(x => x.id === cid);
    if (c) { setCustomerName(c.name); setCustomerPhone(c.phone); }
    else { setCustomerName(""); setCustomerPhone(""); }
  };

  const filtered = useMemo(() => {
    const t = q.toLowerCase().trim();
    if (!t) return products.slice(0, 12);
    return products.filter((p) => p.name.toLowerCase().includes(t) || p.barcode.includes(t)).slice(0, 24);
  }, [products, q]);

  // Sık Kullanılanlar: seed edilen ürünler + kullanıcının işaretlediği favoriler.
  // Kategori bazlı sıralama: Ziynet → Sikke → Bilezik → Gram → Döviz → diğer.
  const SUBCAT_ORDER = { "Ziynet": 1, "Sikke": 2, "Bilezik": 3, "Gram": 4, "Döviz": 5 };
  const favorites = useMemo(() => {
    return products
      .filter((p) => p.is_favorite)
      .sort((a, b) => {
        const ao = SUBCAT_ORDER[a.subcategory] || 99;
        const bo = SUBCAT_ORDER[b.subcategory] || 99;
        if (ao !== bo) return ao - bo;
        return (a.name || "").localeCompare(b.name || "", "tr");
      });
  }, [products]);

  const addToCart = (p) => {
    const unit = priceOf(p, rates, profitMargin, karats);
    // Gram düzenlemesi: sadece "gram-bazlı" ürünlerde (bilezik, düz gram, ya da
    // harem_code'suz özel ürünler). Sikke/ziynet/döviz gibi birim ürünler kilitlidir.
    const gramEditable = !p.harem_code || GRAM_BASIS_HAREM_CODES.has(p.harem_code);
    setCart((c) => {
      const ex = c.find((x) => x.product_id === p.id);
      if (ex) return c.map((x) => x.product_id === p.id ? { ...x, quantity: x.quantity + 1, total: (x.quantity + 1) * (x.unit_price || unit) } : x);
      return [...c, {
        product_id: p.id, name: p.name, quantity: 1, unit_price: unit, total: unit,
        original_unit_price: unit,
        override_type: null, override_value: null,
        _weight: p.net_weight, _purity: p.purity,
        _gram_editable: gramEditable,
        // Snapshot ürünü — gramaj değişince priceOf(...) ile tekrar hesap için.
        _product: {
          metal_type: p.metal_type,
          purity: p.purity,
          net_weight: p.net_weight,
          workmanship_cost: p.workmanship_cost || 0,
          margin_rate: p.margin_rate || 0,
          harem_code: p.harem_code || "",
          manual_price: p.manual_price ?? null,
        },
      }];
    });
  };

  // Gramaj değişince ürünün birim fiyatı ve toplamı KURUŞU KURUŞUNA yeniden hesaplanır.
  // Pazarlık override'ı (yüzde/tutar/final) varsa, yeni birim fiyat üzerine yeniden uygulanır.
  const updateCartWeight = (id, newWeightRaw) => {
    const newWeight = Number(newWeightRaw);
    if (isNaN(newWeight) || newWeight <= 0) return;
    setCart((c) => c.map((x) => {
      if (x.product_id !== id || !x._gram_editable) return x;
      const snap = { ...x._product, net_weight: newWeight };
      const baseUnit = priceOf(snap, rates, profitMargin, karats);
      // Bargain override'ı yeniden uygula
      let finalUnit = baseUnit;
      if (x.override_type === "pct" && x.override_value != null && !isNaN(Number(x.override_value))) {
        finalUnit = Number((baseUnit * (1 + Number(x.override_value) / 100)).toFixed(2));
      } else if (x.override_type === "amount" && x.override_value != null && !isNaN(Number(x.override_value))) {
        finalUnit = Number((baseUnit + Number(x.override_value)).toFixed(2));
      } else if (x.override_type === "final" && x.override_value != null && !isNaN(Number(x.override_value))) {
        finalUnit = Number(x.override_value);
      }
      finalUnit = Math.max(0, Number(finalUnit.toFixed(2)));
      return {
        ...x,
        _weight: newWeight,
        _product: snap,
        original_unit_price: baseUnit,
        unit_price: finalUnit,
        total: finalUnit * x.quantity,
      };
    }));
  };

  // Anlık pazarlık — cart item için % veya ₺ farkı uygulanır. Toplam anında yenilenir.
  const applyBargain = (id, { type, value }) => {
    setCart((c) => c.map((x) => {
      if (x.product_id !== id) return x;
      const base = x.original_unit_price ?? x.unit_price;
      let unit = base;
      if (type === "pct" && value !== "" && !isNaN(Number(value))) {
        unit = base * (1 + Number(value) / 100);
      } else if (type === "amount" && value !== "" && !isNaN(Number(value))) {
        unit = base + Number(value);
      } else if (type === "final" && value !== "" && !isNaN(Number(value))) {
        // "final" — kullanıcı doğrudan final tutar yazdı
        unit = Number(value);
      }
      unit = Math.max(0, Number(unit.toFixed(2)));
      return { ...x, unit_price: unit, total: unit * x.quantity, override_type: type, override_value: value === "" ? null : Number(value) };
    }));
  };

  const resetBargain = (id) => {
    setCart((c) => c.map((x) => x.product_id === id ? {
      ...x,
      unit_price: x.original_unit_price ?? x.unit_price,
      total: (x.original_unit_price ?? x.unit_price) * x.quantity,
      override_type: null, override_value: null,
    } : x));
  };

  const lookupAndAdd = async (code) => {
    const c = String(code || "").trim();
    if (!c) return false;
    try {
      const r = await api.get(`/products/barcode/${encodeURIComponent(c)}`);
      addToCart(r.data);
      toast.success(`${r.data.name} sepete eklendi`);
      return true;
    } catch {
      toast.error(`Barkod bulunamadı: ${c}`);
      return false;
    }
  };

  const barcodeSubmit = async (e) => {
    e.preventDefault();
    if (!q) return;
    const ok = await lookupAndAdd(q);
    if (ok) setQ("");
  };

  const handleScannerHit = async (code) => {
    setScannerOpen(false);
    await lookupAndAdd(code);
  };

  const dec = (id) => setCart((c) => c.flatMap((x) => x.product_id === id
    ? (x.quantity <= 1 ? [] : [{ ...x, quantity: x.quantity - 1, total: x.unit_price * (x.quantity - 1) }])
    : [x]));
  const inc = (id) => setCart((c) => c.map((x) => x.product_id === id ? { ...x, quantity: x.quantity + 1, total: x.unit_price * (x.quantity + 1) } : x));
  const rem = (id) => setCart((c) => c.filter((x) => x.product_id !== id));

  const cartTotal = cart.reduce((s, x) => s + x.total, 0);
  const cartGram = cart.reduce((s, x) => s + (x._weight * x._purity * x.quantity), 0);

  // Ham Harem alış üzerinden hesaplanan taban tutar (kâr / manuel öncesi)
  const scrapBase = useMemo(() => {
    const g = Number(scrapGram || 0);
    const p = Number(scrapPurity || 0);
    const buy = rates.gold?.buying_price || 0;
    return Number((g * p * buy).toFixed(2));
  }, [scrapGram, scrapPurity, rates]);

  // Kâr oranı uygulanmış otomatik tutar: base × (1 - margin/100)
  const scrapAutoTotal = useMemo(() => {
    return Number((scrapBase * (1 - Number(scrapMargin || 0) / 100)).toFixed(2));
  }, [scrapBase, scrapMargin]);

  // Efektif tutar — manuel modda kullanıcı yazdığı, değilse otomatik
  const scrapTotal = scrapManualMode
    ? Number(scrapManualTotal || 0)
    : scrapAutoTotal;

  // Gramaj/ayar değişince manuel modu bırak (yeni bir tartım = yeni pazarlık)
  useEffect(() => {
    if (scrapManualMode) {
      setScrapManualMode(false);
      setScrapManualTotal("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrapGram, scrapPurity]);

  // Manuel tutar değiştiğinde kâr oranını geriye doğru hesapla (Seçenek A — şeffaflık)
  const handleManualTotalChange = (raw) => {
    const v = sanitizeDecimal(raw);
    setScrapManualTotal(v);
    setScrapManualMode(true);
    const num = Number(v || 0);
    if (scrapBase > 0 && num >= 0) {
      const derivedMargin = ((1 - num / scrapBase) * 100);
      // 2 basamak — negatif değerleri de destekle (kullanıcı Harem üstü ödüyorsa)
      setScrapMargin(derivedMargin.toFixed(2));
    }
  };

  // Kâr oranı değişince manuel modu bırak — otomatik canlı hesaba dön
  const handleMarginChange = (raw) => {
    // Kâr oranı negatif de olabilir (Harem üstü alım). "-" ve "," destekle
    const cleaned = raw.replace(/[^\d,\-\.]/g, "").replace(",", ".");
    setScrapMargin(cleaned);
    if (scrapManualMode) {
      setScrapManualMode(false);
      setScrapManualTotal("");
    }
  };

  const resetScrapPricing = () => {
    setScrapMargin("0");
    setScrapManualMode(false);
    setScrapManualTotal("");
  };

  const checkout = async () => {
    try {
      if (type === "sale") {
        if (cart.length === 0) return toast.error("Sepet boş");
        const items = cart.map((x) => ({
          product_id: x.product_id,
          name: x.name,
          quantity: x.quantity,
          unit_price: x.unit_price,
          total: x.total,
          original_unit_price: x.original_unit_price ?? x.unit_price,
          override_type: x.override_type || null,
          override_value: x.override_value ?? null,
          _weight: x._weight,
          _purity: x._purity,
        }));
        const r = await api.post("/transactions", {
          type: "sale",
          customer_id: customerId || null,
          customer_name: customerName || null,
          customer_phone: customerPhone || null,
          items: items.map(({_weight,_purity,...rest}) => rest),
          payment_method: payment,
          total_amount_tl: Number(cartTotal.toFixed(2)),
          total_gold_gram: -Number(cartGram.toFixed(4)),
        });
        setLastTxn({
          id: r.data.id, kind: "sale",
          total: Number(cartTotal.toFixed(2)),
          gram: Number(cartGram.toFixed(4)),
          items, payment,
          customerPhone, customerName,
          date: new Date(),
        });
        toast.success("Satış tamamlandı");
        setCart([]);

        // ── PayTR seçildiyse: dükkanın kendi anahtarlarıyla ödeme linki üret + modal
        if (payment === "paytr") {
          const amt = Number(cartTotal.toFixed(2));
          setPaytrModal({ open: true, amount: amt, data: null, loading: true, error: null });
          try {
            const link = await api.post("/pos/paytr-link", {
              amount_tl: amt,
              customer_name: customerName || "Müşteri",
              customer_email: null,
              customer_phone: customerPhone || null,
              txn_id: r.data.id,
              origin_url: window.location.origin,
            });
            setPaytrModal((m) => ({ ...m, data: link.data, loading: false, error: null }));
          } catch (e) {
            setPaytrModal((m) => ({ ...m, loading: false, error: e?.response?.data?.detail || "Ödeme linki alınamadı" }));
          }
        }
      } else {
        if (!scrapGram || Number(scrapGram) <= 0) return toast.error("Hurda gram girin");
        if (scrapTotal <= 0) return toast.error("Ödenecek tutar geçersiz");
        const marginNum = Number(scrapMargin || 0);
        const noteParts = [
          `Hurda: ${scrapGram} gr @ ${(Number(scrapPurity) * 1000).toFixed(0)}‰`,
          `Taban: ${scrapBase.toFixed(2)}₺`,
          scrapManualMode
            ? `Manuel Pazarlık: ${scrapTotal.toFixed(2)}₺ (kâr ~${marginNum.toFixed(2)}%)`
            : `Kâr Oranı: ${marginNum.toFixed(2)}%`,
        ];
        const r = await api.post("/transactions", {
          type: "scrap_buy",
          customer_id: customerId || null,
          customer_name: customerName || null,
          customer_phone: customerPhone || null,
          items: [],
          payment_method: payment,
          total_amount_tl: scrapTotal,
          total_gold_gram: Number((Number(scrapGram) * Number(scrapPurity)).toFixed(4)),
          note: noteParts.join(" · "),
        });
        setLastTxn({
          id: r.data.id, kind: "scrap",
          total: scrapTotal,
          base: scrapBase,
          marginPct: marginNum,
          manual: scrapManualMode,
          gram: Number(scrapGram) * Number(scrapPurity),
          purity: Number(scrapPurity),
          rawGram: Number(scrapGram),
          items: [], payment,
          customerPhone, customerName,
          date: new Date(),
        });
        toast.success("Hurda alımı kaydedildi");
        setScrapGram("");
        resetScrapPricing();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşlem hatası");
    }
  };

  const printInvoice = () => {
    if (!lastTxnId) return;
    const token = tokenStorage.get("uc_token");
    fetch(`${API}/transactions/${lastTxnId}/invoice.pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(b => window.open(URL.createObjectURL(b), "_blank"));
  };

  // Modal'ın PDF butonu → fatura blob'unu döndürür (indirme/paylaşımı modal yapar)
  const fetchInvoicePdf = async (txnId) => {
    const token = tokenStorage.get("uc_token");
    const lang = (localStorage.getItem("uc_lang") || "tr").toLowerCase();
    const res = await fetch(`${API}/transactions/${txnId}/invoice.pdf?lang=${lang}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error("PDF fetch failed");
    return res.blob();
  };

  const sendWhatsappReceipt = () => {
    if (!lastTxn) return;
    const phone = (lastTxn.customerPhone || "").replace(/[^\d+]/g, "").replace(/^\+/, "");
    if (!phone) return toast.error(t("pos.wa_no_phone"));

    // Fiş metni ve dil seçimi artık modal içindeki `buildReceiptText` +
    // "Dil Seçici" ile üretiliyor. Bu buton sadece modalı açar; ilk metin
    // dükkan sahibinin UI diline göre otomatik hazırlanır.
    setWaModal({
      open: true,
      phone,
      lastTxn,
      customer: lastTxn.customerName || "",
    });
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 lg:gap-6" data-testid="pos-page">
      <div className="xl:col-span-2 space-y-4 lg:space-y-6">
        <div>
          <div className="label-caps">Tezgah</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2 font-light">Satış · POS</h1>
        </div>

        <div className="card-flat p-4 sm:p-6">
          <div className="flex gap-3 mb-4 label-caps text-base sm:text-sm">
            <button onClick={()=>setType("sale")} className={type==="sale"?"text-[--gold]":"text-neutral-500"} data-testid="tab-sale" style={{color: type==="sale"?"var(--gold)":undefined}}>Satış</button>
            <span className="text-neutral-700">/</span>
            <button onClick={()=>setType("scrap_buy")} className={type==="scrap_buy"?"text-[--gold]":"text-neutral-500"} data-testid="tab-scrap" style={{color: type==="scrap_buy"?"var(--gold)":undefined}}>Hurda Alım</button>
          </div>

          {type === "sale" ? (
            <>
              <form onSubmit={barcodeSubmit} className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4">
                <div className="flex-1 min-w-0 flex items-center border border-white/10">
                  <button
                    type="button"
                    onClick={() => setScannerOpen(true)}
                    aria-label="Kamera ile barkod tara"
                    title="Kamera ile barkod tara"
                    data-testid="pos-scanner-open"
                    className="shrink-0 px-3 py-3 border-r border-white/10 text-neutral-400 hover:text-[--gold] hover:bg-white/5 transition-colors active:scale-95"
                  >
                    <ScanLine size={16} />
                  </button>
                  <input
                    data-testid="pos-search"
                    value={q} onChange={(e)=>setQ(e.target.value)}
                    placeholder="Barkod okut veya ürün ara..."
                    className="flex-1 min-w-0 bg-transparent px-3 py-3 text-sm focus:outline-none"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setToolsOpen(true)}
                    aria-label="Hızlı araçlar · Hesap makinesi ve komisyon"
                    title="Hızlı araçlar"
                    data-testid="pos-tools-open"
                    className="shrink-0 px-3 py-3 border-l border-white/10 text-neutral-400 hover:text-[--gold] hover:bg-white/5 transition-colors active:scale-95"
                  >
                    <Zap size={16} />
                  </button>
                </div>
                <button className="btn-gold w-full sm:w-auto shrink-0 sm:min-w-[96px]" data-testid="pos-barcode-submit">Ekle</button>
              </form>

              {favorites.length > 0 && (
                <div className="mb-4" data-testid="pos-favorites">
                  <div className="flex items-center justify-between mb-2">
                    <div className="label-caps text-[10px] tracking-widest" style={{color:"var(--gold)"}}>★ Sık Kullanılanlar</div>
                    <div className="text-[10px] text-neutral-500 tabular-nums">{favorites.length} ürün</div>
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 snap-x scrollbar-thin" style={{scrollbarWidth:"thin"}}>
                    {favorites.map((p) => {
                      const price = priceOf(p, rates, profitMargin, karats);
                      return (
                        <button
                          key={p.id}
                          onClick={() => addToCart(p)}
                          className="shrink-0 snap-start min-w-[136px] sm:min-w-[150px] card-flat px-3 py-2.5 text-left border border-white/10 hover:border-[--gold] transition-all duration-150 hover:-translate-y-0.5"
                          data-testid={`pos-fav-${p.barcode}`}
                          title={`${p.name} · ${fmt4(p.net_weight)} gr`}
                        >
                          <div className="text-[9px] uppercase tracking-wider text-neutral-500 truncate">{p.subcategory || "—"}</div>
                          <div className="text-xs mt-0.5 font-medium truncate">{p.name}</div>
                          <div className="text-[10px] text-neutral-500 tabular-nums mt-0.5">{fmt4(p.net_weight)} gr</div>
                          <div className="font-mono text-xs mt-1 tabular-nums truncate" style={{color:"var(--gold)"}}>{fmt2(price)} ₺</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-3 gap-3 max-h-[52vh] overflow-auto">
                {filtered.map((p) => (
                  <button
                    key={p.id} onClick={()=>addToCart(p)}
                    className="text-left card-flat p-4 hover:border-[--gold] transition-colors duration-200"
                    style={{borderColor:"rgba(255,255,255,0.08)"}}
                    data-testid={`pos-product-${p.barcode}`}
                  >
                    <div className="text-xs text-neutral-500 font-mono">{p.barcode}</div>
                    <div className="text-sm mt-1">{p.name}</div>
                    <div className="flex justify-between mt-3">
                      <span className="label-caps">{(p.purity*1000).toFixed(0)}‰ · {fmt4(p.net_weight)}gr</span>
                      <span className="font-mono text-sm tabular-nums" style={{color:"var(--gold)"}}>{fmt2(priceOf(p, rates, profitMargin, karats))} ₺</span>
                    </div>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label-caps block mb-2">Gram</label>
                <input data-testid="scrap-gram" type="text" inputMode="decimal" value={scrapGram} onChange={(e)=>setScrapGram(sanitizeDecimal(e.target.value))} placeholder="0,0000" className="w-full bg-transparent border border-white/10 px-3 py-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[--gold]" />
              </div>
              <div>
                <label className="label-caps block mb-2">Ayar</label>
                <select data-testid="scrap-purity" value={scrapPurity} onChange={(e)=>setScrapPurity(e.target.value)} className="w-full border border-white/10 px-3 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[--gold]" style={{background:"var(--surface-2)"}}>
                  <option value="0.995">24 Ayar (995)</option>
                  <option value="0.916">22 Ayar (916)</option>
                  <option value="0.750">18 Ayar (750)</option>
                  <option value="0.585">14 Ayar (585)</option>
                </select>
              </div>

              <div>
                <label className="label-caps block mb-2 flex items-center gap-1">
                  <Percent size={12} /> Alım Kârı (%)
                </label>
                <input
                  data-testid="scrap-margin"
                  type="text"
                  inputMode="decimal"
                  value={scrapMargin}
                  onChange={(e)=>handleMarginChange(e.target.value)}
                  placeholder="0"
                  className="w-full bg-transparent border border-white/10 px-3 py-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[--gold]"
                />
                <div className="text-[10px] text-neutral-500 mt-1">(+) Harem altında al · (−) üstünde al</div>
              </div>

              <div>
                <label className="label-caps block mb-2 flex items-center gap-1 justify-between">
                  <span>Ödenecek (Manuel)</span>
                  {scrapManualMode && (
                    <button
                      type="button"
                      onClick={resetScrapPricing}
                      className="text-[10px] uppercase tracking-widest text-[--gold] flex items-center gap-1"
                      data-testid="scrap-manual-reset"
                      style={{color:"var(--gold)"}}
                    >
                      <RefreshCw size={10} /> Sıfırla
                    </button>
                  )}
                </label>
                <input
                  data-testid="scrap-manual-total"
                  type="text"
                  inputMode="decimal"
                  value={scrapManualMode ? scrapManualTotal : fmt2(scrapAutoTotal)}
                  onChange={(e)=>handleManualTotalChange(e.target.value)}
                  onFocus={(e)=>{
                    if (!scrapManualMode) {
                      setScrapManualMode(true);
                      setScrapManualTotal(String(scrapAutoTotal));
                      // seçili gelsin, hızlı üzerine yazsın
                      setTimeout(()=>e.target.select(), 0);
                    }
                  }}
                  placeholder="0,00"
                  className="w-full bg-transparent border px-3 py-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[--gold]"
                  style={{ borderColor: scrapManualMode ? "var(--gold)" : "rgba(255,255,255,0.1)" }}
                />
                <div className="text-[10px] text-neutral-500 mt-1">
                  {scrapManualMode ? "Pazarlık modu — kâr geri hesaplandı" : "Otomatik: taban × kâr"}
                </div>
              </div>

              <div className="col-span-2 border-t border-white/5 pt-4 space-y-2">
                <div className="flex justify-between items-center text-xs text-neutral-500">
                  <span>Harem Alış Taban Bedel</span>
                  <span className="font-mono tabular-nums" data-testid="scrap-base">{fmt2(scrapBase)} ₺</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="label-caps flex items-center gap-2">
                    Hurda Alım Bedeli
                    {scrapManualMode && (
                      <span className="text-[9px] px-1.5 py-0.5 border border-[--gold] tracking-widest" style={{color:"var(--gold)", borderColor:"var(--gold)"}}>MANUEL</span>
                    )}
                  </span>
                  <span className="font-mono text-2xl tabular-nums" style={{color:"var(--gold)"}} data-testid="scrap-total">{fmt2(scrapTotal)} ₺</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card-flat p-4 sm:p-6 h-fit xl:sticky xl:top-40" data-testid="cart-panel">
        <div className="label-caps">Sepet · Ödeme</div>
        <div className="font-display text-2xl mt-1 mb-4">Fatura</div>

        <div className="space-y-3 mb-4">
          <div className="flex gap-2">
            <select value={customerId} onChange={(e)=>pickCustomer(e.target.value)} className="flex-1 min-w-0 border border-white/10 px-3 py-2 text-sm" style={{background:"var(--surface-2)"}} data-testid="customer-picker">
              <option value="">Peşin / Kayıtlı Müşteri Seç...</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name} {c.phone && `· ${c.phone}`}</option>)}
            </select>
            <QuickCustomerAdd
              testid="pos-quick-customer"
              onCreated={(c) => {
                setCustomers((prev) => [...prev, c]);
                setCustomerId(c.id);
                setCustomerName(c.name);
                setCustomerPhone(c.phone || "");
              }}
            />
          </div>
          <input placeholder="Müşteri adı (manuel)" value={customerName} onChange={(e)=>setCustomerName(e.target.value)} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm focus:outline-none" data-testid="customer-name" />
          <input placeholder="532 123 45 67" value={formatPhoneTR(customerPhone)} onChange={(e)=>setCustomerPhone(sanitizePhone(e.target.value))} inputMode="tel" maxLength={13} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm font-mono focus:outline-none" data-testid="customer-phone" />
        </div>

        {type === "sale" && (
          <div className="max-h-72 overflow-auto divide-y divide-white/5 border-t border-b border-white/5">
            {cart.length === 0 && <div className="py-6 text-center text-neutral-500 text-sm">Sepet boş</div>}
            {cart.map((x) => (
              <CartRow key={x.product_id} x={x} dec={dec} inc={inc} rem={rem} applyBargain={applyBargain} resetBargain={resetBargain} updateCartWeight={updateCartWeight} />
            ))}
          </div>
        )}

        <div className="my-4">
          <label className="label-caps block mb-2">Ödeme</label>
          <div className="grid grid-cols-3 gap-2">
            {[["cash","Nakit"],["card","Kart"],["transfer","Havale"]].map(([k,l]) => (
              <button
                key={k}
                onClick={()=>setPayment(k)}
                data-testid={`pay-${k}`}
                className={`border px-3 py-3 sm:py-2 text-xs uppercase tracking-widest transition-colors duration-200 active:scale-95 ${payment===k ? "border-[--gold] text-[--gold]" : "border-white/10 text-neutral-400"}`}
                style={payment===k ? {borderColor:"var(--gold)", color:"var(--gold)"} : {}}
              >{l}</button>
            ))}
          </div>
          {/* Kendi PayTR altyapın — Online tahsilat (kart+QR+link) */}
          {type === "sale" && (
            <button
              onClick={() => setPayment("paytr")}
              data-testid="pay-paytr"
              className={`w-full mt-2 border px-3 py-2.5 text-xs uppercase tracking-widest transition active:scale-95 flex items-center justify-center gap-2 ${payment==="paytr" ? "border-[--gold] text-[--gold] bg-white/5" : "border-white/10 text-neutral-400 hover:border-white/25"}`}
            >
              <span className="font-bold" style={{color: payment==="paytr" ? "var(--gold)" : undefined}}>PayTR</span>
              <span>Kart / QR / Link — Kendi Altyapın</span>
            </button>
          )}
        </div>

        <div className="border-t border-white/5 pt-4">
          <div className="flex justify-between items-baseline mb-2">
            <span className="label-caps">{type==="sale"?"Toplam":"Ödenecek"}</span>
            <span className="font-mono text-3xl tabular-nums" style={{color:"var(--gold)"}} data-testid="cart-total">
              {fmt2(type==="sale" ? cartTotal : scrapTotal)} ₺
            </span>
          </div>
          {type === "sale" && (
            <div className="flex justify-between text-xs text-neutral-500">
              <span>Has Karşılığı</span>
              <span className="font-mono tabular-nums">{fmt4(cartGram)} gr</span>
            </div>
          )}
        </div>

        <button className="btn-primary-gold w-full mt-6" onClick={checkout} data-testid="checkout-btn">
          {type === "sale" ? "Satışı Tamamla" : "Hurdayı Al"}
        </button>

        {lastTxnId && (
          <>
            <button onClick={printInvoice} className="btn-gold w-full mt-3 flex items-center justify-center gap-2" data-testid="print-invoice">
              <Printer size={12} /> Son İşlemin Faturası
            </button>
            <button onClick={sendWhatsappReceipt} className="w-full mt-3 flex items-center justify-center gap-2 py-2.5 text-sm font-medium border border-green-500/40 text-green-300 hover:bg-green-500/10 transition-colors" data-testid="whatsapp-receipt">
              <MessageCircle size={14} /> {t("pos.wa_receipt")}
            </button>
            <div className="text-[10px] text-neutral-600 mt-2 text-center">{t("pos.wa_receipt_hint")}</div>
          </>
        )}
      </div>

      <BarcodeScannerModal
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={handleScannerHit}
      />

      <WhatsAppReceiptModal
        open={waModal.open}
        phone={waModal.phone}
        lastTxn={waModal.lastTxn}
        shopName={shopName}
        shopMeta={shopMeta}
        customerName={waModal.customer}
        fetchInvoicePdf={fetchInvoicePdf}
        onClose={() => setWaModal((s) => ({ ...s, open: false }))}
      />
      <PayTRLinkModal
        open={paytrModal.open}
        amount={paytrModal.amount}
        customerName={customerName}
        customerPhone={customerPhone}
        data={paytrModal.data}
        loading={paytrModal.loading}
        error={paytrModal.error}
        onClose={() => setPaytrModal({ open: false, amount: 0, data: null, loading: false, error: null })}
        onRefresh={async () => {
          setPaytrModal((m) => ({ ...m, loading: true, error: null }));
          try {
            const link = await api.post("/pos/paytr-link", {
              amount_tl: paytrModal.amount,
              customer_name: customerName || "Müşteri",
              customer_phone: customerPhone || null,
              origin_url: window.location.origin,
            });
            setPaytrModal((m) => ({ ...m, data: link.data, loading: false }));
          } catch (e) {
            setPaytrModal((m) => ({ ...m, loading: false, error: e?.response?.data?.detail || "Ödeme linki alınamadı" }));
          }
        }}
      />

      <POSToolsDrawer open={toolsOpen} onClose={() => setToolsOpen(false)} />
    </div>
  );
}


/**
 * Cart row with inline "Anlık Pazarlık" panel — dükkan sahibi satış anında
 * ürünün fiyatını %, ₺ farkı veya doğrudan final tutar olarak override eder.
 * Toplam ve WhatsApp fişi otomatik yansır.
 */
function CartRow({ x, dec, inc, rem, applyBargain, resetBargain, updateCartWeight }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(x.override_type || "pct");
  const [value, setValue] = useState(x.override_value?.toString() || "");
  // Local text state for gram input so the user can type "15." smoothly without
  // React clobbering during onChange re-renders.
  const [gramText, setGramText] = useState(String(x._weight ?? ""));
  useEffect(() => { setGramText(String(x._weight ?? "")); }, [x._weight]);

  // Anlık hesaplama — kullanıcı gramı değiştirdiği anda sepet toplamı güncellensin.
  // Debounce yerine parse-if-valid: değer geçerli pozitif sayıysa doğrudan commit.
  useEffect(() => {
    const w = Number(String(gramText).replace(",", "."));
    if (isNaN(w) || w <= 0) return;
    if (Math.abs(w - Number(x._weight || 0)) < 1e-6) return;
    updateCartWeight?.(x.product_id, w);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gramText]);

  const overridden = !!x.override_type;

  const commitGram = () => {
    const w = Number(String(gramText).replace(",", "."));
    if (isNaN(w) || w <= 0) {
      setGramText(String(x._weight ?? ""));
      return;
    }
    if (Math.abs(w - Number(x._weight || 0)) < 1e-6) return;
    updateCartWeight?.(x.product_id, w);
  };

  const apply = () => {
    applyBargain(x.product_id, { type: mode, value });
    setOpen(false);
  };
  const clear = () => {
    resetBargain(x.product_id);
    setValue("");
  };

  const label = overridden
    ? (x.override_type === "pct"
        ? `${x.override_value > 0 ? "+" : ""}${x.override_value}%`
        : x.override_type === "amount"
          ? `${x.override_value > 0 ? "+" : ""}${x.override_value}₺`
          : "SABİT")
    : null;

  return (
    <div className="py-3">
      <div className="flex justify-between items-center gap-2">
        <div className="min-w-0">
          <div className="text-sm truncate flex items-center gap-2">
            {x.name}
            {label && (
              <span
                className="text-[10px] font-mono px-1.5 py-0.5 border"
                style={{ borderColor: "var(--gold)", color: "var(--gold)" }}
                data-testid={`bargain-tag-${x.product_id}`}
              >
                {label}
              </span>
            )}
          </div>
          <div className="label-caps mt-1 font-mono">
            {fmt2(x.unit_price)} ₺ / {x._gram_editable ? `${fmt4(x._weight)} gr` : "adet"}
            {overridden && (
              <span className="ml-1 text-neutral-600 line-through">{fmt2(x.original_unit_price)} ₺</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <button
            onClick={() => setOpen((o) => !o)}
            aria-label="Pazarlık"
            title="Anlık Pazarlık"
            data-testid={`bargain-toggle-${x.product_id}`}
            className={`w-9 h-9 sm:w-7 sm:h-7 border flex items-center justify-center transition active:scale-95 ${open || overridden ? "border-[--gold] text-[--gold]" : "border-white/15 hover:border-[--gold]"}`}
          >
            <Percent size={14} />
          </button>
          <button onClick={() => dec(x.product_id)} aria-label="Azalt" className="w-9 h-9 sm:w-7 sm:h-7 border border-white/15 flex items-center justify-center hover:border-[--gold] active:scale-95 transition"><Minus size={14} /></button>
          <span className="font-mono text-sm w-7 text-center" data-testid={`cart-qty-${x.product_id}`}>{x.quantity}</span>
          <button onClick={() => inc(x.product_id)} aria-label="Arttır" className="w-9 h-9 sm:w-7 sm:h-7 border border-white/15 flex items-center justify-center hover:border-[--gold] active:scale-95 transition"><Plus size={14} /></button>
          <button onClick={() => rem(x.product_id)} aria-label="Sil" className="w-9 h-9 sm:w-7 sm:h-7 text-red-400 hover:text-red-300 flex items-center justify-center ml-1"><Trash2 size={14} /></button>
        </div>
      </div>

      {x._gram_editable && (
        <div className="mt-2 flex items-center gap-2" data-testid={`gram-editor-${x.product_id}`}>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 shrink-0">Gramaj</label>
          <div className="flex items-center border border-white/15 flex-1 focus-within:border-[--gold] transition-colors">
            <input
              type="text"
              inputMode="decimal"
              value={gramText}
              onChange={(e) => setGramText(sanitizeDecimal(e.target.value))}
              onBlur={commitGram}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitGram(); e.currentTarget.blur(); } }}
              onFocus={(e) => e.currentTarget.select()}
              className="flex-1 min-w-0 bg-transparent px-2.5 py-1.5 text-sm font-mono tabular-nums focus:outline-none"
              data-testid={`cart-weight-input-${x.product_id}`}
              placeholder="0,00"
            />
            <span className="px-2 text-[10px] text-neutral-500 tracking-wider">GR</span>
          </div>
          <span className="text-[10px] text-neutral-500 tabular-nums font-mono shrink-0">
            {(x._purity * 1000).toFixed(0)}‰
          </span>
        </div>
      )}

      {open && (
        <div
          className="mt-2 p-3 border border-[--gold]/40 bg-black/40"
          style={{ borderColor: "rgba(212,175,55,0.35)" }}
          data-testid={`bargain-panel-${x.product_id}`}
        >
          <div className="label-caps mb-2 flex items-center gap-1.5"><Percent size={11} style={{color:"var(--gold)"}} /> Anlık Pazarlık</div>
          <div className="grid grid-cols-3 gap-1 mb-2">
            {[
              ["pct", "%"],
              ["amount", "₺"],
              ["final", "Final"],
            ].map(([k, l]) => (
              <button
                key={k}
                type="button"
                onClick={() => setMode(k)}
                data-testid={`bargain-mode-${x.product_id}-${k}`}
                className={`py-1.5 text-[10px] font-mono tracking-wider uppercase border transition-colors duration-150 ${mode === k ? "border-[--gold] text-[--gold]" : "border-white/15 text-neutral-400 hover:border-white/40"}`}
              >
                {l}
              </button>
            ))}
          </div>
          <div className="flex gap-2 items-center">
            <input
              type="text"
              inputMode="decimal"
              value={value}
              onChange={(e) => setValue(sanitizeDecimal(e.target.value, { maxDecimals: 2 }))}
              onKeyDown={(e) => e.key === "Enter" && apply()}
              placeholder={mode === "pct" ? "Örn: -10" : mode === "amount" ? "Örn: -250" : "Final ₺"}
              className="flex-1 bg-transparent border border-white/15 px-2.5 py-2 text-sm font-mono focus:outline-none focus:border-[--gold]"
              data-testid={`bargain-input-${x.product_id}`}
              autoFocus
            />
            <button
              onClick={apply}
              className="btn-primary-gold px-3 py-2 text-xs"
              data-testid={`bargain-apply-${x.product_id}`}
            >
              Uygula
            </button>
            {overridden && (
              <button
                onClick={clear}
                title="Sıfırla"
                className="w-9 h-9 border border-white/15 text-neutral-400 hover:border-red-400 hover:text-red-300 flex items-center justify-center"
                data-testid={`bargain-clear-${x.product_id}`}
              >
                <RefreshCw size={12} />
              </button>
            )}
          </div>
          <div className="label-caps mt-2 text-neutral-500">
            {mode === "pct" && "Yüzde iskonto/ilave uygulanır (- iskonto, + ilave)"}
            {mode === "amount" && "TL cinsinden fark eklenir (- iskonto, + ilave)"}
            {mode === "final" && "Doğrudan yazdığınız tutar birim fiyat olur"}
          </div>
        </div>
      )}
    </div>
  );
}
